import { RouteComponentProps } from "../../../../models/RouteComponent";
import useApiService from "../../../../hooks/useApiService";
import { AttendanceRecord } from "../../../../models/DSF/SiteAttendance";
import React, { useEffect, useState } from "react";
import { CardActions, CardOverflow, Typography } from "@mui/joy";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { formatDateTime, formatTime } from "../../../../utils/DateUtils";
import { Column } from "primereact/column";
import { Accordion, AccordionTab } from "primereact/accordion";
import { CheckIcon } from "../../../../assets/icons";
import { Dialog } from "primereact/dialog";
import "./styles.scss";

function SiteAttendance(props: RouteComponentProps) {
  const { session } = props;
  const [organisedData, setOrganisedData] = useState<
    { [key: string]: AttendanceRecord[] } | undefined
  >();
  const [displayData, setDisplayData] = useState<AttendanceRecord | null>(null);

  const { loading, error } = useApiService<AttendanceRecord[]>({
    method: "GET",
    url: "site-attendance",
    apiName: "open",
    params: {
      clientId: session.client_id,
      siteId: session.site_id,
    },
    onSuccess: data => {
      // organise data by start_at date and create object with date as pk and array of objects as value
      const organisedData = data.reduce(
        (acc: { [key: string]: AttendanceRecord[] }, item: AttendanceRecord) => {
          const date = item.start_at.split("T")[0];
          if (!acc[date]) {
            acc[date] = [];
          }
          acc[date].push(item);
          return acc;
        },
        {} as { [key: string]: AttendanceRecord[] }
      );

      setOrganisedData(organisedData);
    },
  });

  useEffect(() => {
    props.setGlobalLoader(loading);
  }, [loading]);

  const displayStartTime = (data: AttendanceRecord) => {
    return (
      <>
        <span className="vertical-align-middle ml-2 font-bold line-height-3">
          {formatTime(data.start_at)}
        </span>
      </>
    );
  };

  const displayEndTime = (data: AttendanceRecord) => {
    return (
      <>
        <span className="vertical-align-middle ml-2 font-bold line-height-3">
          {formatTime(data.end_at)}
        </span>
      </>
    );
  };

  const displayIsVisitor = (data: AttendanceRecord) => {
    if (data.employee_id) return "—";
    return (
      <div>
        <CheckIcon fill={"#03c267"} />
      </div>
    );
  };

  return (
    <>
      <Typography level="h2" component="h1">
        Site attendance
      </Typography>
      <CardOverflow
        style={{ width: "100%" }}
        sx={{ borderTop: "1px solid", borderColor: "divider" }}
      >
        <CardActions
          style={{ marginTop: 15, marginBottom: 15 }}
          sx={{ alignSelf: "flex-end", pt: 2 }}
        >
          <Button
            name="button"
            icon="pi pi-save"
            label="Download PDF"
            className="p-button-outlined"
            size="small"
            disabled={true}
            style={{
              position: "absolute",
              fontSize: "small",
              right: 0,
              color: "#2dd36f",
            }}
            onClick={() => {}}
          />
        </CardActions>
      </CardOverflow>
      {error ? (
        <div className="error-message">Could not load site attendance</div>
      ) : (
        <>
          {organisedData && (
            <Accordion multiple activeIndex={[0]}>
              {Object.keys(organisedData).map((key, index) => {
                return (
                  <AccordionTab key={index} header={key}>
                    <div>
                      <DataTable
                        value={organisedData[key]}
                        tableStyle={{ minWidth: "50rem" }}
                        onRowClick={e => {
                          // @ts-ignore
                          setDisplayData(e.data);
                        }}
                        className={"policies-procedures-table"}
                        // className="site-attendance-table"
                      >
                        <Column field={"full_name"} header={"Name"} sortable={false} />
                        <Column
                          field={"employee_id"}
                          header={"Visitor"}
                          body={displayIsVisitor}
                          style={{ width: "5%" }}
                        />
                        <Column
                          field={"start_at"}
                          header={"Time in"}
                          body={displayStartTime}
                          sortable
                          style={{ width: "15%" }}
                        />
                        <Column
                          field={"end_at"}
                          header={"End at"}
                          body={displayEndTime}
                          sortable
                          style={{ width: "15%" }}
                        />
                        <Column
                          field={"reason_for_visit"}
                          header={"Reason for visit"}
                          style={{ width: "40%" }}
                        />
                      </DataTable>
                    </div>
                  </AccordionTab>
                );
              })}
            </Accordion>
          )}
        </>
      )}

      {displayData && (
        <Dialog
          onHide={() => setDisplayData(null)}
          visible={!!displayData}
          header={displayData.full_name}
          style={{ width: "60vw" }}
        >
          <div className={"site-attendance-dialog"}>
            <div className={"section-header section-header-darker"}>{displayData.type} details</div>
            <div className={"responsive-grid-2"}>
              <p>Full name</p>
              <p>{displayData.full_name}</p>
            </div>
            <div className={"responsive-grid-2"}>
              <p>Email</p>
              <p>{displayData.email}</p>
            </div>
            <div className={"responsive-grid-2"}>
              <p>Phone</p>
              <p>{displayData.mobile}</p>
            </div>
            <div className={"responsive-grid-2"}>
              <p>SA ID number</p>
              <p>{displayData.id_number}</p>
            </div>
          </div>
          <div className={"site-attendance-dialog"}>
            <div className={"section-header section-header-darker"}>Attendance details</div>
            <div className={"responsive-grid-2"}>
              <p>Sign in time</p>
              <p>{formatDateTime(displayData.start_at, "short")}</p>
            </div>
            <div className={"responsive-grid-2"}>
              <p>Sign out time</p>
              <p>{formatDateTime(displayData.end_at, "short")}</p>
            </div>
            <div className={"responsive-grid-2"}>
              <p>Time on site</p>
              <p>{displayData.time_on_site}</p>
            </div>
            <div className={"responsive-grid-2"}>
              <p>Reason for visit</p>
              <p>{displayData.reason_for_visit}</p>
            </div>

            <div className={"responsive-grid-2"}>
              <p>Mandatory PPE</p>
              <p>
                {displayData.mandatory_ppe
                  ? displayData.mandatory_ppe.map(ppe => <span>{ppe}, </span>)
                  : "No PPE"}
              </p>
            </div>

            <div className={"responsive-grid-2"}>
              <p>Signature</p>
              <p>
                {displayData.signature ? (
                  <img
                    src={`${displayData.signature}`}
                    alt="Signature"
                    style={{ maxWidth: "200px", height: "auto" }}
                  />
                ) : (
                  "No signature"
                )}
              </p>
            </div>
          </div>
        </Dialog>
      )}
    </>
  );
}

export default SiteAttendance;
